import { storePath } from '../address.js';
import {
  type DesignPage,
  type DesignSection,
  type SectionOf,
  type StoreProduct,
  WEEKDAYS,
  videoEmbed,
} from '@arabat/contracts';
import { Badge, EmptyState, Select, cn } from '@arabat/ui';
import { ErrorState, readableOn, useMoneyFormat } from '@arabat/web-core';
import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useSearchParams } from 'react-router';
import { toMoney, useCart } from '../cart.js';
import {
  type StoreGroup,
  findGroup,
  inGroup,
  useWishlist,
  navGroups,
  quickVariant,
  useBranchChoice,
  useLocalName,
  useSlug,
  useStoreProducts,
  useStorefront,
  RouteGroupContext,
  productPath,
  useStorePaths,
} from '../shared.js';
import { DesignAnchor, StoreIcon } from './parts.js';
import { ProductGridSkeleton } from './skeleton.js';
import {
  assetUrl,
  isPreview,
  useCard,
  useCta,
  useDesign,
  useStyledCta,
  useText,
} from './context.js';
import { SectionFrame, SectionTitle, full, shownSections } from './frame.js';
import { headerHasSearch } from './Header.js';
import { MenuQuickAdd, PageCartBar } from './menu-parts.js';
import { SaleBadge, SalePrice, productSale } from './sale.js';
import {
  CollageSection,
  CountdownSection,
  CtaSection,
  FaqSection,
  GallerySection,
  LogosSection,
  SpacerSection,
  TestimonialsSection,
} from './sections-more.js';
import {
  CardsSection,
  ColumnsSection,
  DividerSection,
  MarqueeSection,
  SplitSection,
  StatsSection,
  StepsSection,
  TabsSection,
} from './sections-layout.js';

/** The designed page a section is drawn on. */
const PageContext = createContext<DesignPage>('home');

/** One designed page (home, products or categories): its shown sections in order. */
export function DesignedPage({ page }: { page: DesignPage }) {
  const design = useDesign();
  const sections = shownSections(design.pages[page]);
  // A link to `/#anchor` scrolls to the section with that anchor (ADR-0128 amendment 7).
  const { hash } = useLocation();
  useEffect(() => {
    if (!hash || hash.length < 2) return;
    const target = document.getElementById(decodeURIComponent(hash.slice(1)));
    if (target && typeof target.scrollIntoView === 'function')
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [hash, sections.length]);
  return (
    <PageContext.Provider value={page}>
      <div className="-my-4 flex flex-col" data-page={page}>
        {sections.map((s) => (
          <SectionFrame key={s.id} section={s}>
            <Section section={s} />
          </SectionFrame>
        ))}
      </div>
      {/* The product page shows its own bar (with or without its sections). */}
      {page === 'product' ? null : <PageCartBar />}
    </PageContext.Provider>
  );
}

function Section({ section: s }: { section: DesignSection }) {
  switch (s.type) {
    case 'slider':
      return <SliderSection s={s} />;
    case 'banner':
      return <BannerSection s={s} />;
    case 'products':
      return <ProductsSection s={s} />;
    case 'categories':
      return <CategoriesSection s={s} />;
    case 'image':
      return <ImageSection s={s} />;
    case 'video':
      return <VideoSection s={s} />;
    case 'text':
      return <TextSection s={s} />;
    case 'features':
      return <FeaturesSection s={s} />;
    case 'menu':
      return <MenuSection s={s} />;
    case 'info':
      return <InfoSection s={s} />;
    case 'catalog':
      return <CatalogSection s={s} />;
    case 'countdown':
      return <CountdownSection s={s} />;
    case 'testimonials':
      return <TestimonialsSection s={s} />;
    case 'faq':
      return <FaqSection s={s} />;
    case 'gallery':
      return <GallerySection s={s} />;
    case 'logos':
      return <LogosSection s={s} />;
    case 'collage':
      return <CollageSection s={s} />;
    case 'cta':
      return <CtaSection s={s} />;
    case 'spacer':
      return <SpacerSection s={s} />;
    case 'divider':
      return <DividerSection s={s} />;
    case 'columns':
      return <ColumnsSection s={s} />;
    case 'split':
      return <SplitSection s={s} />;
    case 'cards':
      return <CardsSection s={s} />;
    case 'tabs':
      return <TabsSection s={s} />;
    case 'marquee':
      return <MarqueeSection s={s} />;
    case 'stats':
      return <StatsSection s={s} />;
    case 'steps':
      return <StepsSection s={s} />;
  }
}

/** Shade over pictures so the words on them stay readable. */
export const OVERLAY = {
  none: '',
  light: 'bg-gradient-to-t from-black/40 via-black/5 to-transparent',
  dark: 'bg-gradient-to-t from-black/70 via-black/20 to-transparent',
} as const;

// ---------------------------------------------------------------------------- slider
function SliderSection({ s }: { s: SectionOf<'slider'> }) {
  const slug = useSlug();
  const text = useText();
  const cta = useCta();
  const { t } = useTranslation();
  const track = useRef<HTMLDivElement>(null);
  const [index, setIndex] = useState(0);
  // The slide the slider is scrolling to by itself (autoplay, arrows, dots): until it gets there
  // the scroll events are its own, not the shopper's swipe, and must not pull it back.
  const steering = useRef<{ to: number; until: number } | null>(null);
  const count = s.slides.length;
  useEffect(() => {
    if (!s.autoplay || count < 2) return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    const timer = setInterval(() => setIndex((i) => (i + 1) % count), s.interval * 1000);
    return () => clearInterval(timer);
  }, [s.autoplay, s.interval, count]);
  useEffect(() => {
    const el = track.current;
    const slide = el?.children[index] as HTMLElement | undefined;
    if (!el || !slide) return;
    // By how far the slide sits from the track's edge on screen: right to left (Arabic) scroll
    // positions are negative, so an offset computed from the left was refused and the slider
    // never moved there.
    const delta = slide.getBoundingClientRect().left - el.getBoundingClientRect().left;
    if (Math.abs(delta) <= 1) return;
    steering.current = { to: index, until: Date.now() + 1500 };
    el.scrollBy?.({ left: delta, behavior: 'smooth' });
  }, [index]);
  if (count === 0) return null;
  const tall = {
    small: 'aspect-[16/10] sm:aspect-[3/1]',
    medium:
      s.variant === 'full' ? 'aspect-[4/5] sm:aspect-[21/8]' : 'aspect-[4/3] sm:aspect-[21/8]',
    large: 'aspect-[4/5] sm:aspect-[16/7]',
    banner: 'aspect-square sm:aspect-[3/1]',
  }[s.height];
  const arrow = (by: -1 | 1) => (
    <button
      type="button"
      aria-label={by < 0 ? t('design.prevSlide') : t('design.nextSlide')}
      onClick={() => setIndex((i) => (i + by + count) % count)}
      className={cn(
        'absolute top-1/2 z-10 grid size-10 -translate-y-1/2 place-items-center rounded-full bg-white/85 text-[#111] shadow-md backdrop-blur transition hover:bg-white max-sm:hidden',
        by < 0 ? 'start-3' : 'end-3',
      )}
    >
      <StoreIcon
        name="chevron"
        className={cn('size-5', by < 0 ? 'rotate-90 rtl:-rotate-90' : '-rotate-90 rtl:rotate-90')}
      />
    </button>
  );
  return (
    <section
      aria-roledescription="carousel"
      aria-label={t('design.slider')}
      className={cn('relative', s.variant === 'full' && full)}
    >
      {count > 1 && s.showArrows ? (
        <>
          {arrow(-1)}
          {arrow(1)}
        </>
      ) : null}
      <div
        ref={track}
        className={cn(
          'flex snap-x snap-mandatory overflow-x-auto [scrollbar-width:none]',
          s.variant === 'cards' ? 'gap-3' : '',
        )}
        onScroll={(e) => {
          const el = e.currentTarget;
          const i = Math.round(
            Math.abs(el.scrollLeft) /
              Math.max(1, el.clientWidth * (s.variant === 'cards' ? 0.85 : 1)),
          );
          const own = steering.current;
          if (own) {
            // Its own smooth scroll: wait until it arrives (or gives up) before following swipes.
            if (i === own.to || Date.now() > own.until) steering.current = null;
            return;
          }
          if (i !== index && i < count) setIndex(i);
        }}
      >
        {s.slides.map((slide, i) => {
          const img = assetUrl(slug, slide.imageId);
          const mobileImg = assetUrl(slug, slide.mobileImageId);
          const title = text(slide.title);
          const subtitle = text(slide.subtitle);
          const button = text(slide.button);
          return (
            <div
              key={slide.id}
              role="group"
              aria-roledescription="slide"
              aria-label={`${i + 1} / ${count}`}
              className={cn(
                'relative shrink-0 snap-start overflow-hidden',
                s.variant === 'cards' ? 'w-[85%] rounded-xl sm:w-[60%]' : 'w-full',
                s.variant === 'contained' && 'rounded-xl',
                tall,
              )}
            >
              {img ? (
                <picture>
                  {mobileImg ? <source media="(max-width: 639px)" srcSet={mobileImg} /> : null}
                  <img
                    src={img}
                    alt={title}
                    className="absolute inset-0 size-full object-cover"
                    loading={i === 0 ? 'eager' : 'lazy'}
                  />
                </picture>
              ) : (
                <div
                  aria-hidden
                  className="absolute inset-0 bg-gradient-to-br from-primary to-primary-hover"
                />
              )}
              {title || subtitle || button ? (
                <div
                  className={cn(
                    'absolute inset-0 flex flex-col justify-end gap-2 p-5 text-white sm:p-10',
                    OVERLAY[s.overlay],
                    s.textAlign === 'center' && 'items-center justify-center text-center',
                  )}
                >
                  {title ? (
                    <p className="max-w-xl text-2xl font-bold sm:text-4xl">{title}</p>
                  ) : null}
                  {subtitle ? <p className="max-w-xl text-sm sm:text-base">{subtitle}</p> : null}
                  {button && slide.link ? (
                    <DesignAnchor link={slide.link} className={cn(cta('light'), 'mt-2 w-fit')}>
                      {button}
                    </DesignAnchor>
                  ) : null}
                </div>
              ) : slide.link ? (
                <DesignAnchor link={slide.link} className="absolute inset-0">
                  <span className="sr-only">{t('design.open')}</span>
                </DesignAnchor>
              ) : null}
            </div>
          );
        })}
      </div>
      {count > 1 ? (
        <div className="mt-3 flex justify-center gap-2">
          {s.slides.map((slide, i) => (
            <button
              key={slide.id}
              type="button"
              aria-label={t('design.goToSlide', { n: i + 1 })}
              aria-current={i === index}
              onClick={() => setIndex(i)}
              className={cn(
                'h-2 rounded-full transition-all',
                i === index ? 'w-6 bg-primary' : 'w-2 bg-border-strong',
              )}
            />
          ))}
        </div>
      ) : null}
    </section>
  );
}

// ---------------------------------------------------------------------------- banner
const BANNER_SHADE = {
  none: '',
  light: 'bg-gradient-to-t from-black/40 via-black/5 to-transparent',
  medium: 'bg-black/35',
  dark: 'bg-gradient-to-t from-black/70 via-black/20 to-transparent',
  gradient:
    'bg-gradient-to-tr from-black/75 via-[color-mix(in_oklch,var(--color-primary)_35%,transparent)] to-transparent',
} as const;
const BANNER_HEIGHT = {
  small: 'min-h-48 sm:min-h-64',
  medium: 'min-h-64 sm:min-h-96',
  large: 'min-h-80 sm:min-h-[32rem]',
  screen: 'min-h-[calc(100svh-4rem)]',
} as const;
const BANNER_TITLE = {
  regular: 'text-2xl sm:text-4xl',
  large: 'text-3xl sm:text-5xl',
  huge: 'text-4xl sm:text-7xl tracking-tight',
} as const;
const ROUNDED = { none: '', soft: 'rounded-xl', round: 'rounded-[2rem]' } as const;

function BannerSection({ s }: { s: SectionOf<'banner'> }) {
  const slug = useSlug();
  const { data: store } = useStorefront();
  const text = useText();
  const button = useStyledCta();
  const img =
    s.imageSource === 'store'
      ? (store?.bannerUrl ?? null)
      : s.imageSource === 'upload'
        ? assetUrl(slug, s.imageId)
        : null;
  const mobile = s.imageSource === 'upload' ? assetUrl(slug, s.mobileImageId) : null;
  const eyebrow = text(s.eyebrow);
  const title = text(s.title);
  const subtitle = text(s.subtitle);
  const label = text(s.button);
  const label2 = text(s.button2);
  const hasText = Boolean(eyebrow || title || subtitle || label || label2);
  if (!img && !hasText) return null;
  const testId = s.imageSource === 'store' ? 'store-banner' : undefined;
  // Edge to edge: the banner leaves the page's width and loses its corners.
  const wide = s.width === 'full';
  const corners = wide ? 'rounded-none' : ROUNDED[s.rounded];
  const align = {
    start: 'items-start text-start',
    center: 'items-center text-center',
    end: 'items-end text-end',
  }[s.align];
  const words = (light: boolean, onColour = false) => (
    <div className={cn('flex flex-col gap-2', align)}>
      {eyebrow ? (
        <p
          className={cn(
            'text-xs font-bold uppercase tracking-[0.2em]',
            light || onColour ? 'opacity-90' : 'text-primary',
          )}
        >
          {eyebrow}
        </p>
      ) : null}
      {title ? (
        <p className={cn('font-bold leading-tight', BANNER_TITLE[s.titleSize])}>{title}</p>
      ) : null}
      {subtitle ? (
        <p
          className={cn(
            'max-w-xl text-sm sm:text-base',
            light ? 'text-white/90' : onColour ? 'opacity-90' : 'text-fg-muted',
          )}
        >
          {subtitle}
        </p>
      ) : null}
      {label || label2 ? (
        <div
          className={cn(
            'mt-2 flex flex-wrap gap-2',
            s.align === 'center' && 'justify-center',
            s.align === 'end' && 'justify-end',
          )}
        >
          {label ? (
            <DesignAnchor
              link={s.link}
              className={cn(
                onColour ? button('outline', false) : button(s.buttonStyle, light),
                'w-fit',
              )}
            >
              {label}
            </DesignAnchor>
          ) : null}
          {label2 ? (
            <DesignAnchor
              link={s.link2}
              className={cn(
                onColour
                  ? 'inline-flex min-h-11 items-center px-3 text-sm font-semibold underline-offset-4 hover:underline'
                  : button(s.buttonStyle, light, false),
                'w-fit',
              )}
            >
              {label2}
            </DesignAnchor>
          ) : null}
        </div>
      ) : null}
    </div>
  );
  // The picture, with another one for phones when chosen.
  const picture = (className: string) =>
    img ? (
      <picture className="contents">
        {mobile ? <source media="(max-width: 639px)" srcSet={mobile} /> : null}
        <img src={img} alt="" className={className} data-testid={testId} />
      </picture>
    ) : null;
  // The store's picture alone (the legacy banner).
  if (!hasText && img)
    return (
      <DesignAnchor link={s.link} className={cn('block', wide && full)} data-banner-width={s.width}>
        {picture(cn('aspect-[3/1] w-full object-cover', corners || 'rounded-none'))}
      </DesignAnchor>
    );
  if (s.variant === 'split')
    return (
      <section
        className={cn(
          'grid items-center gap-6 overflow-hidden bg-surface-muted sm:grid-cols-2',
          corners,
          wide && full,
        )}
        data-banner-width={s.width}
      >
        {picture('aspect-[4/3] size-full object-cover')}
        <div className="p-6 sm:p-10">{words(false)}</div>
      </section>
    );
  if (s.variant === 'color' || !img)
    return (
      <section
        className={cn('bg-primary p-8 text-primary-fg sm:p-12', corners, wide && full)}
        data-banner-width={s.width}
      >
        {words(false, true)}
      </section>
    );
  const position = {
    top: 'justify-start',
    center: 'justify-center',
    bottom: 'justify-end',
  }[s.position];
  const onCard = s.variant === 'card' || s.variant === 'glass';
  return (
    <section
      className={cn('relative isolate overflow-hidden', corners, wide && full)}
      data-banner-width={s.width}
    >
      {picture(cn('absolute inset-0 -z-10 size-full object-cover', s.zoom && 'store-zoom'))}
      <div
        className={cn(
          'relative flex flex-col p-6 sm:p-12',
          !onCard && 'text-white',
          BANNER_HEIGHT[s.height],
          position,
          !onCard && BANNER_SHADE[s.overlay],
          // Older centred banners keep their even shade.
          !onCard && s.align === 'center' && s.overlay !== 'none' && s.overlay !== 'medium'
            ? 'bg-black/40'
            : null,
          onCard && s.overlay !== 'none' && 'bg-black/20',
          s.align === 'center' && 'items-center',
          s.align === 'end' && 'items-end',
        )}
      >
        {onCard ? (
          <div
            className={cn(
              'max-w-xl rounded-2xl p-6 sm:p-8',
              s.variant === 'glass'
                ? 'border border-white/30 bg-white/15 text-white shadow-2xl backdrop-blur-xl'
                : 'bg-surface text-fg shadow-2xl',
            )}
            data-banner-card
          >
            {words(s.variant === 'glass')}
          </div>
        ) : (
          words(true)
        )}
      </div>
    </section>
  );
}

// --------------------------------------------------------------------------- products
const lowestPrice = (p: StoreProduct) =>
  p.variants.map((v) => toMoney(v.price)).reduce((a, b) => (b.compare(a) < 0 ? b : a));

/** How product cards look in a section (ADR-0128 amendment 7). */
export type CardLook = Pick<
  SectionOf<'products'>,
  | 'cardStyle'
  | 'aspect'
  | 'hoverImage'
  | 'imageFit'
  | 'showCategory'
  | 'nameLines'
  | 'priceSize'
  | 'textAlign'
  | 'buyButton'
  | 'buyLabel'
  | 'buyColor'
  | 'soldOut'
> &
  Partial<
    Pick<SectionOf<'products'>, 'newBadgeDays' | 'wishlist' | 'dateBadge' | 'dateBadgeColor'>
  >;
export const DEFAULT_LOOK: CardLook = {
  cardStyle: 'default',
  aspect: 'square',
  hoverImage: false,
  imageFit: 'cover',
  showCategory: false,
  nameLines: 'full',
  priceSize: 'regular',
  textAlign: 'start',
  buyButton: 'none',
  buyLabel: { en: null, ar: null },
  buyColor: null,
  soldOut: 'text',
};
const ASPECT = { square: 'aspect-square', portrait: 'aspect-[4/5]', landscape: 'aspect-[4/3]' };

/** `2026-10-27` → `27/10/2026`, the way shops in Kuwait write a date (ADR-0147). */
export function formatDay(day: string): string {
  const [y, m, d] = day.split('-');
  return y && m && d ? `${d}/${m}/${y}` : day;
}

/** Added to the catalog in the last `days` days (ADR-0146); 0 = the badge is off. */
function isNew(p: StoreProduct, days: number): boolean {
  if (days <= 0 || !p.createdAt) return false;
  return Date.now() - Date.parse(p.createdAt) < days * 86_400_000;
}

/** The heart on a card (ADR-0146): keeps the product in this browser's favourites. */
function WishlistButton({ productId }: { productId: string }) {
  const { t } = useTranslation();
  const { has, toggle } = useWishlist();
  const on = has(productId);
  return (
    <button
      type="button"
      aria-pressed={on}
      aria-label={on ? t('design.wishlistRemove') : t('design.wishlistAdd')}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        toggle(productId);
      }}
      className="absolute top-2 end-2 z-[2] grid size-9 place-items-center rounded-full bg-white/90 text-[#111] shadow"
      data-testid="wishlist-button"
    >
      <svg
        viewBox="0 0 24 24"
        className="size-5"
        aria-hidden
        fill={on ? 'currentColor' : 'none'}
        stroke="currentColor"
        strokeWidth="2"
      >
        <path d="M12 21s-7.5-4.6-9.3-9.2C1.4 8.4 3.6 5 7 5c2 0 3.4 1.1 5 3 1.6-1.9 3-3 5-3 3.4 0 5.6 3.4 4.3 6.8C19.5 16.4 12 21 12 21z" />
      </svg>
    </button>
  );
}

/**
 * The card's buy button: adds a product with a single option at once (and says so), and opens
 * the page of a product with options.
 */
function BuyButton({
  p,
  look,
  available,
}: {
  p: StoreProduct;
  look: CardLook;
  available: boolean;
}) {
  const cart = useCart();
  const slug = useSlug();
  const text = useText();
  const name = useLocalName();
  const { t } = useTranslation();
  const [added, setAdded] = useState(false);
  useEffect(() => {
    if (!added) return;
    const timer = setTimeout(() => setAdded(false), 1500);
    return () => clearTimeout(timer);
  }, [added]);
  const label = text(look.buyLabel) || t('design.addToCart');
  const style = look.buyColor
    ? { backgroundColor: look.buyColor, color: readableOn(look.buyColor) }
    : undefined;
  const className = cn(
    'inline-flex items-center justify-center gap-1 rounded-md font-semibold transition-opacity hover:opacity-90 disabled:opacity-50',
    !look.buyColor && 'bg-primary text-primary-fg',
    look.buyButton === 'full' ? 'min-h-11 w-full px-4 text-sm' : 'min-h-9 shrink-0 px-3 text-xs',
  );
  const single = quickVariant(p);
  if (!single)
    return (
      <Link to={storePath(slug, productPath(p))} className={className} style={style}>
        {t('design.chooseOptions')}
      </Link>
    );
  return (
    <button
      type="button"
      disabled={!available}
      aria-label={`${label}: ${name(p)}`}
      onClick={() => {
        cart.add(
          { variantId: single.variantId, productId: p.productId, quantity: 1 },
          single.maxQuantity,
        );
        setAdded(true);
      }}
      className={className}
      style={style}
    >
      {added ? `✓ ${t('design.added')}` : label}
    </button>
  );
}

export function ProductCard({
  p,
  variant = 'grid',
  quickAdd = false,
  look = DEFAULT_LOOK,
  pictureAdd = false,
}: {
  p: StoreProduct;
  variant?: 'grid' | 'list';
  quickAdd?: boolean;
  /** The interactive + (then − n +) on the picture's bottom corner (owner, 2026-11-02). */
  pictureAdd?: boolean;
  look?: CardLook;
}) {
  const slug = useSlug();
  const name = useLocalName();
  const money = useMoneyFormat();
  const card = useCard();
  const { data: store } = useStorefront();
  const { t, i18n } = useTranslation();
  const available = p.variants.some((v) => v.available);
  const prices = p.variants.map((v) => toMoney(v.price));
  const lowest = lowestPrice(p);
  const varies = prices.some((m) => !m.equals(lowest));
  const price = varies ? t('catalog.from', { price: money(lowest) }) : money(lowest);
  const sale = productSale(p);
  const description = i18n.language.startsWith('ar')
    ? p.descriptionAr || p.descriptionEn
    : p.descriptionEn || p.descriptionAr;
  // One-tap add (owner, 2026-11-02): the menu's + that opens − n + and folds into the quantity,
  // on the picture's bottom corner of a card; at the card's corner in a list.
  if (quickAdd && look.buyButton === 'none' && variant === 'grid')
    return <ProductCard p={p} variant={variant} look={look} pictureAdd />;
  if (quickAdd && look.buyButton === 'none')
    return (
      <div className="relative h-full">
        <ProductCard p={p} variant={variant} look={look} />
        <span className="absolute bottom-3 end-3 z-10">
          <MenuQuickAdd product={p} />
        </span>
      </div>
    );
  if (variant === 'list')
    return (
      <Link
        to={storePath(slug, productPath(p))}
        className={cn('flex gap-3 overflow-hidden rounded-lg p-2 hover:border-primary', card)}
      >
        <div className="size-20 shrink-0 overflow-hidden rounded-md bg-surface-muted">
          {p.imageUrl ? (
            <img src={p.imageUrl} alt="" className="size-full object-cover" loading="lazy" />
          ) : null}
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-1 py-1">
          <p className="font-medium">{name(p)}</p>
          {description ? <p className="line-clamp-2 text-xs text-fg-muted">{description}</p> : null}
          <p className="mt-auto text-sm font-semibold tabular-nums">
            <SalePrice sale={sale}>{price}</SalePrice>
          </p>
          {!available ? <Badge tone="warning">{t('catalog.unavailable')}</Badge> : null}
        </div>
      </Link>
    );
  const to = storePath(slug, productPath(p));
  const contain = look.imageFit === 'contain';
  const second = look.hoverImage ? (p.images[1] ?? null) : null;
  const corner =
    !available && look.soldOut === 'corner' ? (
      <span
        className={cn(
          'absolute end-2 z-[1] rounded-full bg-fg/55 px-2.5 py-0.5 text-xs font-semibold text-canvas',
          // Under the heart when the card has one.
          look.wishlist ? 'top-12' : 'top-2',
        )}
      >
        {t('design.soldOut')}
      </span>
    ) : null;
  const pictureBox = (
    <div
      className={cn(
        'relative overflow-hidden',
        contain ? 'bg-white' : 'bg-surface-muted',
        ASPECT[look.aspect],
      )}
    >
      {p.imageUrl ? (
        <img
          src={p.imageUrl}
          alt=""
          className={cn(
            'size-full transition-all duration-500',
            contain ? 'object-contain p-3' : 'object-cover',
            second ? 'group-hover:opacity-0' : 'group-hover:scale-105',
          )}
          loading="lazy"
        />
      ) : null}
      {second ? (
        <img
          src={second}
          alt=""
          data-hover-image
          className={cn(
            'absolute inset-0 size-full opacity-0 transition-opacity duration-500 group-hover:opacity-100',
            contain ? 'object-contain p-3' : 'object-cover',
          )}
          loading="lazy"
        />
      ) : null}
      <SaleBadge sale={sale} where="card" />
      {corner}
      {look.dateBadge && look.dateBadge !== 'off' && p.roastDate ? (
        <span
          className="absolute top-2 start-2 z-[1] rounded-full px-2.5 py-0.5 text-xs font-bold text-white tabular-nums"
          style={{ backgroundColor: look.dateBadgeColor ?? '#dc2626' }}
          data-testid="date-badge"
        >
          {look.dateBadge === 'date'
            ? formatDay(p.roastDate)
            : t(`design.dateBadge_${look.dateBadge}`, { date: formatDay(p.roastDate) })}
        </span>
      ) : null}
      {isNew(p, look.newBadgeDays ?? 0) ? (
        <span
          className={cn(
            'absolute start-2 z-[1] rounded-full bg-primary px-2.5 py-0.5 text-xs font-semibold text-primary-fg',
            look.dateBadge && look.dateBadge !== 'off' && p.roastDate ? 'top-9' : 'top-2',
          )}
          data-testid="new-badge"
        >
          {t('design.newBadge')}
        </span>
      ) : null}
      {look.wishlist ? <WishlistButton productId={p.productId} /> : null}
      {pictureAdd ? (
        <span
          className="absolute bottom-2 end-2 z-[3]"
          data-testid="picture-add"
          // The card is a link: taps on the + / − n + stay on the card.
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <MenuQuickAdd product={p} />
        </span>
      ) : null}
    </div>
  );
  if (look.cardStyle === 'overlay')
    return (
      <Link
        to={to}
        className="group relative flex h-full flex-col overflow-hidden rounded-lg"
        data-card="overlay"
      >
        {pictureBox}
        <div className="absolute inset-x-0 bottom-0 flex flex-col gap-0.5 bg-gradient-to-t from-black/75 to-transparent p-3 pt-10 text-white">
          <p className="font-semibold">{name(p)}</p>
          <p className="text-sm tabular-nums opacity-90">
            <SalePrice sale={sale} oldClassName="text-white/75">
              {price}
            </SalePrice>
          </p>
          {!available && look.soldOut === 'text' ? (
            <Badge tone="warning">{t('catalog.unavailable')}</Badge>
          ) : null}
        </div>
      </Link>
    );
  const category =
    look.showCategory && p.categoryId
      ? store?.categories.find((c) => c.id === p.categoryId)
      : undefined;
  const center = look.textAlign === 'center';
  const priceLine = (
    <p
      className={cn(
        'tabular-nums',
        look.priceSize === 'large' ? 'text-lg font-bold' : 'text-sm text-fg-muted',
      )}
    >
      <SalePrice sale={sale} className={sale ? 'font-semibold' : undefined}>
        {price}
      </SalePrice>
    </p>
  );
  // The card's link holds the picture and the words; the buy button sits beside it, never in it.
  return (
    <div
      className={cn(
        'group flex h-full flex-col overflow-hidden',
        look.cardStyle !== 'divided' && 'rounded-lg',
        look.cardStyle === 'default' && cn(card, 'hover:border-primary'),
        look.cardStyle === 'bordered' && 'border-2 border-border bg-surface hover:border-primary',
        look.cardStyle === 'soft' && 'bg-surface-muted transition-transform hover:-translate-y-1',
        look.cardStyle === 'minimal' && 'bg-transparent',
        look.cardStyle === 'divided' && 'bg-surface',
        center && 'text-center',
      )}
      data-card={look.cardStyle}
    >
      <Link to={to} className="flex flex-1 flex-col">
        <div className={cn(look.cardStyle === 'minimal' && 'overflow-hidden rounded-lg')}>
          {pictureBox}
        </div>
        <div
          className={cn(
            'flex flex-1 flex-col gap-1',
            look.cardStyle === 'minimal' ? 'px-0.5 pt-2' : 'px-3 pt-3',
            look.buyButton === 'none' && (look.cardStyle === 'minimal' ? 'pb-2' : 'pb-3'),
          )}
        >
          {category ? (
            <p className="text-[0.7rem] font-semibold uppercase tracking-wide text-fg-muted">
              {name(category)}
            </p>
          ) : null}
          <p
            className={cn(
              'font-medium',
              look.nameLines === '1' && 'truncate',
              look.nameLines === '2' && 'line-clamp-2 min-h-[2.75em]',
            )}
          >
            {name(p)}
          </p>
          {look.buyButton !== 'inline' ? priceLine : null}
          {!available && look.soldOut === 'text' ? (
            <Badge tone="warning">{t('catalog.unavailable')}</Badge>
          ) : null}
        </div>
      </Link>
      {look.buyButton === 'inline' ? (
        <div
          className={cn(
            'flex items-center gap-3',
            center ? 'justify-center' : 'justify-between',
            look.cardStyle === 'minimal' ? 'px-0.5 pb-2 pt-1' : 'px-3 pb-3 pt-1',
          )}
        >
          {priceLine}
          <BuyButton p={p} look={look} available={available} />
        </div>
      ) : look.buyButton === 'full' ? (
        <div className={cn(look.cardStyle === 'minimal' ? 'pb-2 pt-3' : 'p-3 pt-3')}>
          <BuyButton p={p} look={look} available={available} />
        </div>
      ) : null}
    </div>
  );
}

const COLS: Record<number, string> = {
  2: 'sm:grid-cols-2',
  3: 'sm:grid-cols-3',
  4: 'sm:grid-cols-3 lg:grid-cols-4',
  5: 'sm:grid-cols-3 lg:grid-cols-5',
  6: 'sm:grid-cols-3 lg:grid-cols-6',
};

export function ProductGrid({
  products,
  testId,
  columns = 4,
  variant = 'grid',
  quickAdd = false,
  look = DEFAULT_LOOK,
  mobileColumns = 2,
}: {
  products: StoreProduct[];
  testId?: string;
  columns?: number;
  variant?: 'grid' | 'list' | 'carousel';
  quickAdd?: boolean;
  look?: CardLook;
  mobileColumns?: 1 | 2;
}) {
  if (variant === 'carousel')
    return (
      <ul
        className="-mx-4 flex snap-x gap-3 overflow-x-auto px-4 pb-2 [scrollbar-width:none]"
        data-testid={testId}
      >
        {products.map((p) => (
          <li key={p.productId} className="w-40 shrink-0 snap-start sm:w-52">
            <ProductCard p={p} quickAdd={quickAdd} look={look} />
          </li>
        ))}
      </ul>
    );
  return (
    <ul
      className={cn(
        'grid',
        // Divided cards share one frame: each card's 1px ring meets its neighbour's in the
        // 1px gap, so the lines stay single and a short row leaves no empty block.
        look.cardStyle === 'divided' && variant !== 'list'
          ? 'gap-px p-px [&>li]:bg-surface [&>li]:shadow-[0_0_0_1px_var(--color-border)]'
          : 'gap-3',
        variant === 'list'
          ? 'sm:grid-cols-2'
          : cn(mobileColumns === 1 ? 'grid-cols-1' : 'grid-cols-2', COLS[columns] ?? COLS[4]),
      )}
      data-testid={testId}
    >
      {products.map((p) => (
        <li key={p.productId}>
          <ProductCard
            p={p}
            variant={variant === 'list' ? 'list' : 'grid'}
            quickAdd={quickAdd}
            look={look}
          />
        </li>
      ))}
    </ul>
  );
}

function ProductsSection({ s }: { s: SectionOf<'products'> }) {
  const paths = useStorePaths();
  const products = useStoreProducts();
  const text = useText();
  const slug = useSlug();
  const name = useLocalName();
  const { t } = useTranslation();
  // Tabs (ADR-0146): the shopper picks the source; "Load more" shows `limit` more each time.
  const [tab, setTab] = useState(0);
  const [pages, setPages] = useState(1);
  if (!products.data)
    return products.isPending ? (
      <ProductGridSkeleton
        columns={s.columns}
        mobileColumns={s.mobileColumns}
        count={Math.min(s.limit, s.columns)}
        list={s.variant === 'list'}
      />
    ) : null;
  const all = products.data.products;
  const source =
    s.tabs.length > 0 ? (s.tabs[Math.min(tab, s.tabs.length - 1)] ?? s.source) : s.source;
  const chosen =
    source === 'newest'
      ? [...all].sort(
          (a, b) =>
            // The latest roast first when the merchant gives dates (ADR-0147), then the newest added.
            (b.roastDate ?? '').localeCompare(a.roastDate ?? '') ||
            (b.createdAt ?? '').localeCompare(a.createdAt ?? ''),
        )
      : source === 'featured'
        ? all.filter((p) => p.featured)
        : source === 'category'
          ? all.filter((p) => (s.categoryId ? inGroup(p, s.categoryId) : p.categoryId === null))
          : source === 'manual'
            ? s.productIds
                .map((id) => all.find((p) => p.productId === id))
                .filter((p): p is StoreProduct => Boolean(p))
            : source === 'bestSellers'
              ? all
                  .filter((p) => p.soldRank !== null)
                  .sort((a, b) => (a.soldRank ?? 0) - (b.soldRank ?? 0))
              : all;
  const sorted =
    s.sort === 'position' || source === 'newest'
      ? chosen
      : [...chosen].sort((a, b) =>
          s.sort === 'name'
            ? name(a).localeCompare(name(b))
            : s.sort === 'priceAsc'
              ? lowestPrice(a).compare(lowestPrice(b))
              : lowestPrice(b).compare(lowestPrice(a)),
        );
  const shown = sorted.slice(0, s.limit * (s.loadMore ? pages : 1));
  const tabs =
    s.tabs.length > 0 ? (
      <div
        role="tablist"
        aria-label={text(s.title) || undefined}
        className="mb-4 flex flex-wrap justify-center gap-2"
      >
        {s.tabs.map((x, i) => (
          <button
            key={x}
            type="button"
            role="tab"
            aria-selected={i === tab}
            onClick={() => {
              setTab(i);
              setPages(1);
            }}
            className={cn(
              'rounded-full border px-4 py-1.5 text-sm font-semibold transition-colors',
              i === tab ? 'border-current' : 'border-transparent text-fg-muted hover:text-fg',
            )}
          >
            {t(`design.tab_${x}`)}
          </button>
        ))}
      </div>
    ) : null;
  // Before the first sales the preview says why the best sellers are not there yet.
  if (shown.length === 0 && s.tabs.length === 0)
    return source === 'bestSellers' && isPreview() ? (
      <p className="rounded-xl border border-dashed border-border p-4 text-center text-sm text-fg-muted">
        {t('design.noBestSellers')}
      </p>
    ) : null;
  const more =
    s.source === 'category' && s.categoryId
      ? storePath(slug, paths.group(s.categoryId))
      : storePath(slug, `/products`);
  return (
    <section aria-label={text(s.title) || undefined}>
      <SectionTitle
        aside={
          s.showViewAll ? (
            <Link to={more} className="shrink-0 text-sm font-semibold text-primary hover:underline">
              {t('design.seeAll')}
            </Link>
          ) : undefined
        }
      >
        {text(s.title)}
      </SectionTitle>
      {tabs}
      <ProductGrid
        products={shown}
        columns={s.columns}
        variant={s.variant}
        quickAdd={s.showAdd}
        look={s}
        mobileColumns={s.mobileColumns}
      />
      {s.loadMore && sorted.length > shown.length ? (
        <div className="mt-6 flex justify-center">
          <button
            type="button"
            onClick={() => setPages((n) => n + 1)}
            className="rounded-full border border-dashed border-border px-6 py-2 text-sm text-fg-muted hover:text-fg"
            data-testid="load-more"
          >
            {t('design.loadMore')}
          </button>
        </div>
      ) : null}
    </section>
  );
}

// ------------------------------------------------------------------------- categories
function CategoriesSection({ s }: { s: SectionOf<'categories'> }) {
  const paths = useStorePaths();
  const page = useContext(PageContext);
  const slug = useSlug();
  const { data: store } = useStorefront();
  const products = useStoreProducts();
  const name = useLocalName();
  const text = useText();
  const card = useCard();
  const { t } = useTranslation();
  if (!store) return null;
  const parentOf = (id: string) => store.categories.find((c) => c.id === id)?.parentId ?? null;
  // Chosen ids may name categories or collections; without a choice, the categories.
  const list: StoreGroup[] =
    s.categoryIds.length > 0
      ? s.categoryIds.map((id) => findGroup(store, id)).filter((c): c is StoreGroup => Boolean(c))
      : navGroups(store).filter((c) => !s.topOnly || !parentOf(c.id));
  // On the categories page an empty store says so; elsewhere the section just stays out.
  if (list.length === 0)
    return page === 'categories' ? <EmptyState title={t('design.noCategories')} /> : null;
  const to = (id: string) => storePath(slug, paths.group(id));
  const all = products.data?.products ?? [];
  // A category counts its own products and those of its subcategories.
  const familyOf = (id: string) => [
    id,
    ...store.categories.filter((c) => c.parentId === id).map((c) => c.id),
  ];
  const count = (id: string) => {
    const ids = familyOf(id);
    return all.filter((p) => ids.some((x) => inGroup(p, x))).length;
  };
  const children = (id: string) =>
    s.showSub ? store.categories.filter((c) => c.parentId === id) : [];
  const countLabel = (id: string) =>
    s.showCount && products.data ? t('design.productCount', { count: count(id) }) : null;
  const subLinks = (id: string, className?: string) => {
    const subs = children(id);
    if (subs.length === 0) return null;
    return (
      <ul
        className={cn('flex flex-wrap gap-1.5', className)}
        aria-label={t('design.subcategories')}
      >
        {subs.map((c) => (
          <li key={c.id}>
            <Link
              to={to(c.id)}
              className="inline-block rounded-full bg-surface-muted px-2.5 py-0.5 text-xs hover:text-primary"
            >
              {name(c)}
            </Link>
          </li>
        ))}
      </ul>
    );
  };
  const initial = (label: string) => (
    <span aria-hidden className="text-2xl font-bold text-primary">
      {label.slice(0, 1)}
    </span>
  );
  const phone = { 1: 'grid-cols-1', 2: 'grid-cols-2', 3: 'grid-cols-3' }[s.mobileColumns];
  const grid = {
    2: 'sm:grid-cols-2',
    3: 'sm:grid-cols-3',
    4: 'sm:grid-cols-3 lg:grid-cols-4',
    5: 'sm:grid-cols-3 lg:grid-cols-5',
    6: 'sm:grid-cols-4 lg:grid-cols-6',
  }[s.columns];
  const ratio = { landscape: 'aspect-[4/3]', square: 'aspect-square', portrait: 'aspect-[3/4]' }[
    s.aspect
  ];
  const picture = (c: StoreGroup, className: string) =>
    c.imageUrl ? (
      <img
        src={c.imageUrl}
        alt=""
        className={cn(
          'size-full object-cover transition-transform duration-300',
          s.hover === 'zoom' && 'group-hover:scale-105',
          className,
        )}
      />
    ) : (
      <span className={cn('grid size-full place-items-center bg-primary-soft', className)}>
        {initial(name(c))}
      </span>
    );
  const lift = s.hover === 'lift' && 'transition-transform duration-300 hover:-translate-y-1';
  // A tile: the name over the picture, or under it.
  const tile = (c: StoreGroup) =>
    s.labelPosition === 'below' ? (
      <Link to={to(c.id)} className={cn('group flex flex-col gap-2', lift)}>
        <span className={cn('block overflow-hidden rounded-lg bg-surface-muted', ratio)}>
          {picture(c, '')}
        </span>
        <span className="text-sm font-semibold">
          {name(c)}
          {countLabel(c.id) ? (
            <span className="block text-xs font-normal text-fg-muted">{countLabel(c.id)}</span>
          ) : null}
        </span>
      </Link>
    ) : (
      <Link
        to={to(c.id)}
        className={cn('group relative block overflow-hidden rounded-lg', ratio, card, lift)}
      >
        <span className="absolute inset-0">{picture(c, '')}</span>
        <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-3 text-sm font-semibold text-white">
          {name(c)}
          {countLabel(c.id) ? (
            <span className="block text-xs font-normal opacity-90">{countLabel(c.id)}</span>
          ) : null}
        </span>
      </Link>
    );
  return (
    <section aria-label={text(s.title) || t('nav.categories')} data-variant={s.variant}>
      <SectionTitle
        aside={
          s.showViewAll && page !== 'categories' ? (
            <Link
              to={storePath(slug, `/categories`)}
              className="shrink-0 text-sm font-semibold text-primary hover:underline"
            >
              {t('design.seeAll')}
            </Link>
          ) : undefined
        }
      >
        {text(s.title)}
      </SectionTitle>
      {s.variant === 'carousel' ? (
        <ul className="-mx-4 flex snap-x gap-3 overflow-x-auto px-4 pb-2 [scrollbar-width:none]">
          {list.map((c) => (
            <li key={c.id} className="w-40 shrink-0 snap-start sm:w-56">
              {tile(c)}
            </li>
          ))}
        </ul>
      ) : s.variant === 'circles' ? (
        <ul className="-mx-4 flex gap-4 overflow-x-auto px-4 pb-2 [scrollbar-width:none]">
          {list.map((c) => (
            <li key={c.id} className="shrink-0">
              <Link
                to={to(c.id)}
                className="group flex w-20 flex-col items-center gap-2 text-center text-xs font-medium sm:w-24"
              >
                <span className="grid size-20 place-items-center overflow-hidden rounded-full bg-primary-soft ring-2 ring-transparent transition group-hover:ring-primary sm:size-24">
                  {picture(c, '')}
                </span>
                {name(c)}
                {countLabel(c.id) ? (
                  <span className="text-[11px] text-fg-muted">{countLabel(c.id)}</span>
                ) : null}
              </Link>
            </li>
          ))}
        </ul>
      ) : s.variant === 'chips' ? (
        <ul className="flex flex-wrap gap-2">
          {list.map((c) => (
            <li key={c.id}>
              <Link
                to={to(c.id)}
                className="inline-flex items-center gap-2 rounded-full border border-border bg-surface py-1 pe-4 ps-1 text-sm font-medium hover:border-primary"
              >
                {c.imageUrl ? (
                  <img src={c.imageUrl} alt="" className="size-7 rounded-full object-cover" />
                ) : (
                  <span className="w-2" aria-hidden />
                )}
                {name(c)}
                {s.showCount && products.data ? (
                  <span className="text-xs text-fg-muted">({count(c.id)})</span>
                ) : null}
              </Link>
            </li>
          ))}
        </ul>
      ) : s.variant === 'list' ? (
        <ul className={cn('divide-y divide-border overflow-hidden rounded-lg', card)}>
          {list.map((c) => (
            <li key={c.id} className="flex flex-col gap-2 p-3">
              <Link to={to(c.id)} className="group flex items-center gap-3 hover:text-primary">
                <span className="grid size-12 shrink-0 place-items-center overflow-hidden rounded-md bg-primary-soft">
                  {picture(c, '')}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block font-medium">{name(c)}</span>
                  {countLabel(c.id) ? (
                    <span className="block text-xs text-fg-muted">{countLabel(c.id)}</span>
                  ) : null}
                </span>
                <span aria-hidden className="text-fg-subtle">
                  {/* ‹ › are mirrored by the browser in Arabic; no extra flip. */}›
                </span>
              </Link>
              {subLinks(c.id, 'ps-15')}
            </li>
          ))}
        </ul>
      ) : s.variant === 'banners' ? (
        <ul className="flex flex-col gap-3">
          {list.map((c) => (
            <li key={c.id}>
              <Link
                to={to(c.id)}
                className="group relative flex aspect-[5/2] items-end overflow-hidden rounded-xl sm:aspect-[4/1]"
              >
                <span className="absolute inset-0">{picture(c, '')}</span>
                <span className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                <span className="relative flex w-full items-end justify-between gap-3 p-4 text-white">
                  <span>
                    <span className="block text-lg font-bold sm:text-2xl">{name(c)}</span>
                    {countLabel(c.id) ? (
                      <span className="block text-sm opacity-90">{countLabel(c.id)}</span>
                    ) : null}
                  </span>
                  <span className="shrink-0 rounded-full bg-white/90 px-4 py-1.5 text-sm font-semibold text-[#111]">
                    {t('design.shopNow')}
                  </span>
                </span>
              </Link>
            </li>
          ))}
        </ul>
      ) : s.variant === 'cards' ? (
        <ul className={cn('grid gap-3', phone, grid)}>
          {list.map((c) => (
            <li key={c.id} className={cn('flex flex-col overflow-hidden rounded-lg', card, lift)}>
              <Link to={to(c.id)} className="group flex flex-col">
                <span className={cn('block overflow-hidden bg-surface-muted', ratio)}>
                  {picture(c, '')}
                </span>
                <span className="flex flex-col gap-0.5 p-3">
                  <span className="font-semibold">{name(c)}</span>
                  {countLabel(c.id) ? (
                    <span className="text-xs text-fg-muted">{countLabel(c.id)}</span>
                  ) : null}
                </span>
              </Link>
              {subLinks(c.id, 'px-3 pb-3')}
            </li>
          ))}
        </ul>
      ) : (
        <ul className={cn('grid gap-3', phone, grid)}>
          {list.map((c) => (
            <li key={c.id} className="flex flex-col gap-2">
              {tile(c)}
              {subLinks(c.id)}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ---------------------------------------------------------------- image, video, text
function ImageSection({ s }: { s: SectionOf<'image'> }) {
  const slug = useSlug();
  const text = useText();
  const img = assetUrl(slug, s.imageId);
  if (!img) return null;
  const aspect = { wide: 'aspect-[21/9]', square: 'aspect-square', tall: 'aspect-[3/4]', auto: '' }[
    s.aspect
  ];
  const picture = (
    <img
      src={img}
      alt={text(s.alt)}
      className={cn('w-full object-cover', aspect, s.variant === 'contained' && 'rounded-xl')}
      loading="lazy"
    />
  );
  return (
    <figure className={cn(s.variant === 'full' && full)}>
      {s.link ? (
        <DesignAnchor link={s.link} className="block">
          {picture}
        </DesignAnchor>
      ) : (
        picture
      )}
    </figure>
  );
}

function VideoSection({ s }: { s: SectionOf<'video'> }) {
  const text = useText();
  const { t } = useTranslation();
  const embed = s.url ? videoEmbed(s.url) : null;
  if (!embed) return null;
  const title = text(s.title);
  return (
    <section className={cn(s.variant === 'full' && full)}>
      <SectionTitle>{title}</SectionTitle>
      <div
        className={cn(
          'overflow-hidden bg-black',
          s.variant === 'contained' && 'rounded-xl',
          s.aspect === 'portrait' ? 'mx-auto aspect-[9/16] max-w-sm' : 'aspect-video',
        )}
      >
        <iframe
          src={embed.src}
          title={title || t('design.video')}
          className="size-full"
          loading="lazy"
          allow="accelerometer; encrypted-media; gyroscope; picture-in-picture; fullscreen"
          referrerPolicy="strict-origin-when-cross-origin"
          sandbox="allow-scripts allow-same-origin allow-presentation allow-popups"
          data-testid="video-embed"
        />
      </div>
    </section>
  );
}

function TextSection({ s }: { s: SectionOf<'text'> }) {
  const text = useText();
  const card = useCard();
  const cta = useCta();
  const title = text(s.title);
  const body = text(s.body);
  const button = text(s.button);
  if (!title && !body) return null;
  return (
    <section
      className={cn(
        'flex flex-col gap-2',
        s.align === 'center' && 'items-center text-center',
        s.variant === 'card' && cn('rounded-xl p-6', card),
        s.variant === 'highlight' && 'rounded-xl bg-primary-soft p-6',
      )}
    >
      {title ? <h2 className="text-xl font-bold">{title}</h2> : null}
      {body ? <p className="max-w-3xl whitespace-pre-line text-fg-muted">{body}</p> : null}
      {button && s.link ? (
        <DesignAnchor link={s.link} className={cn(cta('primary'), 'mt-2 w-fit')}>
          {button}
        </DesignAnchor>
      ) : null}
    </section>
  );
}

const ICON_BOX = { small: 'size-9 text-lg', regular: 'size-11 text-xl', large: 'size-16 text-3xl' };

function FeaturesSection({ s }: { s: SectionOf<'features'> }) {
  const text = useText();
  const card = useCard();
  if (s.items.length === 0) return null;
  const stacked = s.align === 'center' || (s.align === 'auto' && s.variant === 'grid');
  const columns =
    s.columns === 0
      ? 'grid-cols-2 lg:grid-cols-3'
      : {
          2: 'grid-cols-1 sm:grid-cols-2',
          3: 'grid-cols-2 lg:grid-cols-3',
          4: 'grid-cols-2 lg:grid-cols-4',
        }[s.columns];
  return (
    <section aria-label={text(s.title) || undefined}>
      <SectionTitle>{text(s.title)}</SectionTitle>
      <ul
        className={cn(
          s.variant === 'list' ? 'flex flex-col divide-y divide-border' : 'grid gap-3',
          s.variant === 'row' && 'grid-cols-1 sm:grid-flow-col sm:auto-cols-fr',
          (s.variant === 'grid' || s.variant === 'cards') && columns,
        )}
      >
        {s.items.map((f) => {
          const body = (
            <>
              {f.icon ? (
                <span
                  aria-hidden
                  data-icon-style={s.iconStyle}
                  className={cn(
                    'grid shrink-0 place-items-center',
                    s.iconStyle !== 'plain' && ICON_BOX[s.iconSize],
                    s.iconStyle === 'plain' &&
                      { small: 'text-xl', regular: 'text-3xl', large: 'text-5xl' }[s.iconSize],
                    s.iconStyle === 'circle' && 'rounded-full bg-surface',
                    s.iconStyle === 'square' && 'rounded-xl bg-primary text-primary-fg',
                    s.iconStyle === 'outline' && 'rounded-full border-2 border-primary',
                  )}
                >
                  {f.icon}
                </span>
              ) : null}
              <span className="min-w-0">
                <span className="block font-semibold">{text(f.title)}</span>
                {text(f.text) ? (
                  <span className="block text-sm text-fg-muted">{text(f.text)}</span>
                ) : null}
              </span>
            </>
          );
          const box = cn(
            'flex items-center gap-3',
            s.variant === 'list' ? 'py-3' : 'rounded-lg p-4',
            s.variant === 'cards' ? card : s.variant !== 'list' && 'bg-surface-muted',
            stacked && 'flex-col text-center',
          );
          return (
            <li key={f.id}>
              {f.link ? (
                <DesignAnchor link={f.link} className={cn(box, 'h-full hover:text-primary')}>
                  {body}
                </DesignAnchor>
              ) : (
                <div className={cn(box, 'h-full')}>{body}</div>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

// ------------------------------------------------------------------- restaurant menu
function MenuSection({ s }: { s: SectionOf<'menu'> }) {
  const { data: store } = useStorefront();
  const products = useStoreProducts();
  const name = useLocalName();
  const text = useText();
  const money = useMoneyFormat();
  const cart = useCart();
  const card = useCard();
  const { t, i18n } = useTranslation();
  const [active, setActive] = useState<string | null>(null);
  if (!store || !products.data)
    return products.isPending ? <ProductGridSkeleton list count={4} /> : null;
  const all = products.data.products;
  const categories = (
    s.categoryIds.length > 0 ? s.categoryIds.map((id) => findGroup(store, id)) : navGroups(store)
  )
    .filter((c): c is StoreGroup => Boolean(c))
    .map((c) => ({ ...c, items: all.filter((p) => inGroup(p, c.id)) }))
    .filter((c) => c.items.length > 0);
  const others =
    s.categoryIds.length > 0
      ? []
      : all.filter((p) => !navGroups(store).some((c) => inGroup(p, c.id)));
  const groups = [
    ...categories,
    ...(others.length > 0
      ? [
          {
            id: 'other',
            nameEn: t('design.other'),
            nameAr: t('design.other'),
            imageUrl: null,
            items: others,
          },
        ]
      : []),
  ];
  if (groups.length === 0) return <EmptyState title={t('catalog.empty')} />;
  const ar = i18n.language.startsWith('ar');
  return (
    <section aria-label={text(s.title) || t('nav.menu')}>
      <SectionTitle>{text(s.title)}</SectionTitle>
      {groups.length > 1 ? (
        <nav
          aria-label={t('design.menuCategories')}
          className={cn(
            '-mx-4 mb-4 flex gap-2 overflow-x-auto bg-canvas/95 px-4 py-2 backdrop-blur [scrollbar-width:none]',
            // Under the sticky header, whatever its height (big logos, tall headers); at the very
            // top when the header scrolls away.
            s.stickyTabs && 'sticky top-[var(--store-header-h,0px)] z-20',
          )}
        >
          {groups.map((g) => (
            <a
              key={g.id}
              href={`#menu-${s.id}-${g.id}`}
              onClick={() => setActive(g.id)}
              className={cn(
                'shrink-0 rounded-full border px-4 py-1.5 text-sm font-medium whitespace-nowrap',
                active === g.id
                  ? 'border-primary bg-primary text-primary-fg'
                  : 'border-border bg-surface',
              )}
            >
              {name(g)}
            </a>
          ))}
        </nav>
      ) : null}
      <div className="flex flex-col gap-8">
        {groups.map((g) => (
          <div
            key={g.id}
            id={`menu-${s.id}-${g.id}`}
            // A tapped tab brings the group's heading under the header and the tabs.
            className="scroll-mt-[calc(var(--store-header-h,0px)+4.5rem)]"
          >
            <h3 className="mb-3 text-lg font-bold">{name(g)}</h3>
            <ul
              className={cn(
                'grid gap-3',
                s.variant === 'grid'
                  ? 'grid-cols-2 sm:grid-cols-3'
                  : s.variant === 'compact'
                    ? ''
                    : s.variant === 'delivery'
                      ? 'gap-0 divide-y divide-border sm:grid-cols-2 sm:gap-x-6 sm:divide-y-0'
                      : 'sm:grid-cols-2',
              )}
            >
              {g.items.map((p) => {
                const single = quickVariant(p);
                const description = ar
                  ? p.descriptionAr || p.descriptionEn
                  : p.descriptionEn || p.descriptionAr;
                const available = p.variants.some((v) => v.available);
                const lowest = lowestPrice(p);
                const add = s.quickAdd ? (
                  <MenuQuickAdd product={p} />
                ) : single && single.available ? (
                  <button
                    type="button"
                    aria-label={t('design.addOne', { name: name(p) })}
                    onClick={(e) => {
                      e.preventDefault();
                      cart.add(
                        { variantId: single.variantId, productId: p.productId, quantity: 1 },
                        single.maxQuantity,
                      );
                    }}
                    className="grid size-9 shrink-0 place-items-center rounded-full bg-primary text-lg font-bold text-primary-fg shadow"
                  >
                    +
                  </button>
                ) : null;
                if (s.variant === 'delivery')
                  return (
                    <li
                      key={p.productId}
                      className="relative flex gap-3 py-3 sm:border-b sm:border-border"
                      data-menu-row
                    >
                      <div className="flex min-w-0 flex-1 flex-col gap-1">
                        <Link
                          to={storePath(store.slug, productPath(p))}
                          className="font-bold after:absolute after:inset-0"
                        >
                          {name(p)}
                        </Link>
                        {s.showDescriptions && description ? (
                          <p className="line-clamp-2 text-sm text-fg-muted">{description}</p>
                        ) : null}
                        <span className="mt-auto pt-1 font-semibold tabular-nums">
                          {p.variants.length > 1
                            ? t('catalog.from', { price: money(lowest) })
                            : money(lowest)}
                        </span>
                        {!available ? (
                          <Badge tone="warning" className="w-fit">
                            {t('catalog.unavailable')}
                          </Badge>
                        ) : null}
                      </div>
                      {s.showImages ? (
                        <div className="relative size-28 shrink-0">
                          <div className="size-full overflow-hidden rounded-xl bg-surface-muted">
                            {p.imageUrl ? (
                              <img
                                src={p.imageUrl}
                                alt=""
                                className="size-full object-cover"
                                loading="lazy"
                              />
                            ) : null}
                          </div>
                          {available ? (
                            <span className="absolute bottom-2 end-2 z-10">{add}</span>
                          ) : null}
                        </div>
                      ) : available ? (
                        <span className="relative z-10 self-end">{add}</span>
                      ) : null}
                    </li>
                  );
                if (s.variant === 'compact')
                  return (
                    <li
                      key={p.productId}
                      className="flex items-center gap-3 border-b border-dashed border-border py-2"
                    >
                      <Link
                        to={storePath(store.slug, productPath(p))}
                        className="min-w-0 flex-1 font-medium hover:underline"
                      >
                        {name(p)}
                      </Link>
                      <span className="tabular-nums text-fg-muted">{money(lowest)}</span>
                      {add}
                    </li>
                  );
                return (
                  <li
                    key={p.productId}
                    className={cn(
                      'relative overflow-hidden rounded-lg',
                      card,
                      s.variant === 'grid' ? 'flex flex-col' : 'flex gap-3 p-3',
                    )}
                  >
                    {s.showImages ? (
                      <div
                        className={cn(
                          'overflow-hidden bg-surface-muted',
                          s.variant === 'grid' ? 'aspect-square' : 'size-24 shrink-0 rounded-md',
                        )}
                      >
                        {p.imageUrl ? (
                          <img
                            src={p.imageUrl}
                            alt=""
                            className="size-full object-cover"
                            loading="lazy"
                          />
                        ) : null}
                      </div>
                    ) : null}
                    <div
                      className={cn(
                        'flex min-w-0 flex-1 flex-col gap-1',
                        s.variant === 'grid' && 'p-3',
                      )}
                    >
                      <Link
                        to={storePath(store.slug, productPath(p))}
                        className="font-semibold after:absolute after:inset-0"
                      >
                        {name(p)}
                      </Link>
                      {s.showDescriptions && description ? (
                        <p className="line-clamp-2 text-xs text-fg-muted">{description}</p>
                      ) : null}
                      <div className="mt-auto flex items-center justify-between gap-2 pt-1">
                        <span className="font-semibold tabular-nums">
                          {p.variants.length > 1
                            ? t('catalog.from', { price: money(lowest) })
                            : money(lowest)}
                        </span>
                        {!available ? (
                          <Badge tone="warning">{t('catalog.unavailable')}</Badge>
                        ) : (
                          <span className="relative z-10">{add}</span>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>
    </section>
  );
}

// -------------------------------------------------------------------------- store info
function InfoSection({ s }: { s: SectionOf<'info'> }) {
  const { data: store } = useStorefront();
  const { branch } = useBranchChoice();
  const name = useLocalName();
  const money = useMoneyFormat();
  const card = useCard();
  const { t } = useTranslation();
  if (!store || !branch) return null;
  const today = WEEKDAYS[(new Date().getDay() + 6) % 7]!;
  const hoursToday = branch.hours ? branch.hours[today] : null;
  const items: { key: string; icon: string; label: string; value: string }[] = [];
  if (s.showHours)
    items.push({
      key: 'open',
      icon: branch.openNow ? '🟢' : '🔴',
      label: branch.openNow ? t('info.openNow') : t('info.closedNow'),
      value:
        hoursToday === null
          ? t('info.alwaysOpen')
          : hoursToday.length === 0
            ? t('info.closedDay')
            : hoursToday.map((r) => `${r.open}–${r.close}`).join(', '),
    });
  if (s.showDelivery) {
    if (branch.preparationMinutes > 0)
      items.push({
        key: 'prep',
        icon: '⏱️',
        label: t('info.ready'),
        value: t('info.minutes', { count: branch.preparationMinutes }),
      });
    if (store.deliveryEnabled)
      items.push({
        key: 'delivery',
        icon: '🛵',
        label: t('info.delivery'),
        value: store.deliveryAreas.length > 0 ? t('info.byArea') : money(store.deliveryFee),
      });
    if (store.pickupEnabled)
      items.push({
        key: 'pickup',
        icon: '🛍️',
        label: t('info.pickup'),
        value: t('info.available'),
      });
    if (!toMoney(store.minimumOrder).isZero())
      items.push({
        key: 'min',
        icon: '🧾',
        label: t('info.minimum'),
        value: money(store.minimumOrder),
      });
  }
  if (s.showContact && store.contactPhone)
    items.push({ key: 'phone', icon: '📞', label: t('info.call'), value: store.contactPhone });
  return (
    <section aria-label={t('info.title')} className="flex flex-col gap-3">
      {items.length > 0 ? (
        <ul
          className={cn(
            s.variant === 'strip'
              ? '-mx-4 flex gap-2 overflow-x-auto px-4 [scrollbar-width:none]'
              : 'grid grid-cols-2 gap-3 sm:grid-cols-4',
          )}
        >
          {items.map((i) => (
            <li
              key={i.key}
              className={cn(
                'flex shrink-0 items-center gap-2 rounded-full px-4 py-2 text-sm',
                s.variant === 'strip'
                  ? 'bg-surface-muted'
                  : cn('flex-col rounded-lg p-4 text-center', card),
              )}
            >
              <span aria-hidden>{i.icon}</span>
              <span className="font-semibold">{i.label}</span>
              <span className="text-fg-muted" dir="auto">
                {i.value}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
      {s.showBranches && store.branches.length > 0 ? (
        <ul className="grid gap-3 sm:grid-cols-2">
          {store.branches.map((b) => (
            <li key={b.id} className={cn('rounded-lg p-4 text-sm', card)}>
              <p className="font-semibold">{name(b)}</p>
              {b.address ? <p className="text-fg-muted">{b.address}</p> : null}
              {b.phone ? (
                <a href={`tel:${b.phone}`} dir="ltr" className="text-primary hover:underline">
                  {b.phone}
                </a>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}

// ---------------------------------------------------------------------------- catalog
/** Search, sort and category filter over every product (the legacy catalog page). */
export function CatalogSection({ s }: { s: SectionOf<'catalog'> }) {
  const { data: store } = useStorefront();
  const products = useStoreProducts();
  const name = useLocalName();
  const text = useText();
  const { t } = useTranslation();
  const [pages, setPages] = useState(1);
  const [params] = useSearchParams();
  // A group page (`/categories/{handle}`…, migration 0107) names its group; `/products` may
  // filter by `?category=` (the sale page).
  const routeGroup = useContext(RouteGroupContext);
  const fromUrl = routeGroup ?? params.get('category');
  const [category, setCategory] = useState<string | null>(fromUrl);
  // The header's search bar opens this page with `?q=`.
  const [searchText, setSearch] = useState(params.get('q') ?? '');
  const [sort, setSort] = useState<'default' | 'name' | 'priceAsc' | 'priceDesc'>(s.defaultSort);
  // When the header has the search, it is the only search box: the list follows its `?q=`.
  const { header } = useDesign();
  const ownSearch = s.showSearch && !headerHasSearch(header);
  const wantsSearch = params.get('search') === '1' && ownSearch;
  useEffect(() => {
    if (wantsSearch) document.querySelector<HTMLInputElement>('input[type="search"]')?.focus();
  }, [wantsSearch]);
  // A link to another category of the same page (drawer, footer) changes the filter.
  const query = params.get('q');
  const [seen, setSeen] = useState(fromUrl);
  const [seenQuery, setSeenQuery] = useState(query);
  if (seenQuery !== query) {
    setSeenQuery(query);
    setSearch(query ?? '');
  }
  if (seen !== fromUrl) {
    setSeen(fromUrl);
    setCategory(fromUrl);
  }

  if (products.isPending)
    return (
      <div className="flex flex-col gap-4">
        {/* The bar's place too, so nothing moves when the products arrive. */}
        {ownSearch || s.showSort || s.showCategories ? (
          <span
            aria-hidden
            className={cn(
              'block animate-pulse rounded-lg bg-surface-muted',
              s.barLayout === 'stacked' || s.searchWidth === 'full' ? 'h-24' : 'h-10',
            )}
          />
        ) : null}
        <ProductGridSkeleton
          columns={s.columns}
          mobileColumns={s.mobileColumns}
          list={s.variant === 'list'}
        />
      </div>
    );
  if (products.isError)
    return <ErrorState error={products.error} onRetry={() => void products.refetch()} />;

  const term = searchText.trim().toLowerCase();
  const sorted = [...products.data.products].sort((a, b) => {
    if (sort === 'name') return name(a).localeCompare(name(b));
    if (sort === 'priceAsc') return lowestPrice(a).compare(lowestPrice(b));
    if (sort === 'priceDesc') return lowestPrice(b).compare(lowestPrice(a));
    return a.position - b.position;
  });
  const visible = sorted.filter(
    (p) =>
      (!category || inGroup(p, category)) &&
      (!term || `${p.nameEn ?? ''} ${p.nameAr ?? ''}`.toLowerCase().includes(term)),
  );
  // Featured products open the page while nothing is filtered (ADR-0121).
  const featured = s.showFeatured && !category && !term ? visible.filter((p) => p.featured) : [];
  // The buttons are the store's sections (its categories). A special list (collection) is not a
  // button of its own (ADR-0130 amendment 1); one opened from a link is shown while it is chosen.
  const all = products.data.products;
  const groups = navGroups(store).filter((c) => all.some((p) => inGroup(p, c.id)));
  const chosen = category && store ? findGroup(store, category) : undefined;
  const categories: StoreGroup[] =
    chosen && !groups.some((c) => c.id === chosen.id) ? [...groups, chosen] : groups;

  if (products.data.products.length === 0) return <EmptyState title={t('catalog.empty')} />;
  const options = [
    { id: null, label: t('catalog.all'), imageUrl: null },
    ...categories.map((c) => ({ id: c.id, label: name(c), imageUrl: c.imageUrl })),
  ] as { id: string | null; label: string; imageUrl: string | null }[];
  const choose = (id: string | null) => {
    setCategory(id);
    setPages(1);
  };
  // The bar's look (ADR-0128 amendment 7).
  const height = { small: 'h-9 text-sm', regular: 'h-10 text-sm', large: 'h-12 text-base' }[
    s.controlSize
  ];
  const shape = { pill: 'rounded-full', rounded: 'rounded-lg', square: 'rounded-none' }[
    s.chipShape
  ];
  const field = cn(
    'w-full text-fg placeholder:text-fg-subtle focus:outline-none',
    height,
    s.searchStyle === 'outline' &&
      'rounded-md border border-border bg-surface px-3 focus:border-primary',
    s.searchStyle === 'filled' &&
      cn(
        'rounded-lg border border-transparent px-3 focus:border-primary focus:bg-surface',
        // On a soft bar the filled box takes the page colour, so it still stands out.
        s.barStyle === 'soft' ? 'bg-surface' : 'bg-surface-muted',
      ),
    s.searchStyle === 'pill' &&
      'rounded-full border border-border bg-surface px-4 focus:border-primary',
    s.searchStyle === 'underline' &&
      'rounded-none border-0 border-b-2 border-border bg-transparent px-1 focus:border-primary',
    s.searchIcon && 'ps-9',
  );
  const select = cn(
    height,
    'cursor-pointer pe-8',
    s.searchStyle === 'outline' && 'rounded-md border border-border bg-surface ps-3',
    s.searchStyle === 'filled' &&
      cn(
        'rounded-lg border border-transparent ps-3',
        s.barStyle === 'soft' ? 'bg-surface' : 'bg-surface-muted',
      ),
    s.searchStyle === 'pill' && 'rounded-full border border-border bg-surface ps-4',
    s.searchStyle === 'underline' &&
      'rounded-none border-0 border-b-2 border-border bg-transparent ps-1',
  );
  const chip = (active: boolean) =>
    cn(
      'flex shrink-0 items-center gap-2 border text-sm font-medium transition-colors',
      shape,
      s.controlSize === 'small' ? 'py-0.5' : s.controlSize === 'large' ? 'py-2 text-base' : 'py-1',
      !active && 'border-border bg-surface hover:border-primary',
      active && s.chipActive === 'solid' && 'border-primary bg-primary text-primary-fg',
      active && s.chipActive === 'outline' && 'border-2 border-primary bg-surface text-primary',
      active && s.chipActive === 'soft' && 'border-transparent bg-primary-soft text-primary',
    );
  const filters =
    s.showCategories && categories.length > 0 ? (
      s.filterStyle === 'dropdown' ? (
        <Select
          aria-label={t('catalog.category')}
          className={cn('w-48', select)}
          value={category ?? ''}
          onChange={(e) => choose(e.target.value || null)}
        >
          {options.map((c) => (
            <option key={c.id ?? 'all'} value={c.id ?? ''}>
              {c.label}
            </option>
          ))}
        </Select>
      ) : (
        <div
          className={cn(
            'flex gap-2',
            s.filterStyle === 'tabs'
              ? '-mb-px w-full overflow-x-auto border-b border-border [scrollbar-width:none]'
              : s.chipScroll
                ? 'max-sm:-mx-4 max-sm:overflow-x-auto max-sm:px-4 max-sm:[scrollbar-width:none] sm:flex-wrap'
                : 'flex-wrap',
            s.barAlign === 'center' && s.filterStyle !== 'tabs' && 'sm:justify-center',
          )}
          role="group"
          data-filter-style={s.filterStyle}
        >
          {options.map((c) =>
            s.filterStyle === 'tabs' ? (
              <button
                key={c.id ?? 'all'}
                type="button"
                aria-pressed={category === c.id}
                onClick={() => choose(c.id)}
                className="shrink-0 border-b-2 border-transparent px-3 py-2 text-sm font-semibold text-fg-muted aria-pressed:border-primary aria-pressed:text-fg"
              >
                {c.label}
              </button>
            ) : (
              <button
                key={c.id ?? 'all'}
                type="button"
                aria-pressed={category === c.id}
                onClick={() => choose(c.id)}
                className={cn(chip(category === c.id), s.chipImages ? 'pe-3 ps-1' : 'px-3')}
                data-chip-shape={s.chipShape}
              >
                {s.chipImages ? (
                  c.imageUrl ? (
                    <img
                      src={c.imageUrl}
                      alt=""
                      className={cn(
                        'size-6 object-cover',
                        shape === 'rounded-none' ? '' : 'rounded-full',
                      )}
                    />
                  ) : (
                    <span className="w-2" aria-hidden />
                  )
                ) : null}
                {c.label}
              </button>
            ),
          )}
        </div>
      )
    ) : null;
  const sortOptions = ['default', 'name', 'priceAsc', 'priceDesc'] as const;
  const sorter = s.showSort ? (
    s.sortStyle === 'pills' ? (
      <div role="group" aria-label={t('catalog.sort')} className="flex flex-wrap gap-1.5">
        {sortOptions.map((o) => (
          <button
            key={o}
            type="button"
            aria-pressed={sort === o}
            onClick={() => setSort(o)}
            className={cn(chip(sort === o), 'px-3')}
          >
            {t(`catalog.sort_${o}`)}
          </button>
        ))}
      </div>
    ) : (
      <Select
        aria-label={t('catalog.sort')}
        className={cn('w-44', select)}
        value={sort}
        onChange={(e) => setSort(e.target.value as typeof sort)}
      >
        {sortOptions.map((o) => (
          <option key={o} value={o}>
            {t(`catalog.sort_${o}`)}
          </option>
        ))}
      </Select>
    )
  ) : null;
  const search = ownSearch ? (
    <div className={cn('relative', s.searchWidth === 'full' ? 'w-full flex-1' : 'w-full max-w-xs')}>
      {s.searchIcon ? (
        <svg
          aria-hidden
          viewBox="0 0 24 24"
          className="pointer-events-none absolute top-1/2 start-3 size-4 -translate-y-1/2 text-fg-subtle"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <circle cx="11" cy="11" r="7" />
          <path d="m20 20-3.5-3.5" />
        </svg>
      ) : null}
      <input
        type="search"
        aria-label={t('catalog.search')}
        placeholder={text(s.searchPlaceholder) || t('catalog.search')}
        value={searchText}
        onChange={(e) => {
          setSearch(e.target.value);
          setPages(1);
        }}
        className={field}
        data-search-style={s.searchStyle}
      />
    </div>
  ) : null;
  const perPage = s.pageSize === 0 ? visible.length : s.pageSize;
  const page = visible.slice(0, perPage * pages);
  const stacked = s.barLayout === 'stacked' || s.searchWidth === 'full';
  return (
    <div className="flex flex-col gap-4">
      {search || sorter || filters ? (
        <div
          className={cn(
            'flex gap-3',
            stacked ? 'flex-col' : 'flex-wrap items-center',
            s.barAlign === 'center' && (stacked ? 'items-center text-center' : 'justify-center'),
            s.barStyle === 'card' && 'rounded-2xl border border-border bg-surface p-3 shadow-sm',
            s.barStyle === 'soft' && 'rounded-2xl bg-surface-muted p-3',
            s.stickyFilters &&
              cn(
                'sticky top-[var(--store-header-h,0px)] z-10',
                s.barStyle === 'plain' && '-mx-4 bg-canvas/90 px-4 py-2 backdrop-blur-md',
              ),
          )}
          data-sticky-filters={s.stickyFilters || undefined}
          data-bar={`${s.barLayout}-${s.barStyle}`}
        >
          {stacked ? (
            <>
              {search || sorter ? (
                <div
                  className={cn(
                    'flex w-full flex-wrap items-center gap-2',
                    s.barAlign === 'center' && 'justify-center',
                  )}
                >
                  {search}
                  {sorter}
                </div>
              ) : null}
              {filters}
            </>
          ) : (
            <>
              {search}
              {sorter}
              {filters}
            </>
          )}
        </div>
      ) : null}
      {featured.length > 0 ? (
        <section aria-label={t('catalog.featured')} className="flex flex-col gap-2">
          <h2 className="text-lg font-semibold">{t('catalog.featured')}</h2>
          <ProductGrid
            products={featured}
            testId="featured-grid"
            columns={s.columns}
            look={s}
            mobileColumns={s.mobileColumns}
            quickAdd={s.showAdd}
          />
        </section>
      ) : null}
      {s.showCount ? (
        <p className="text-sm text-fg-muted" data-testid="catalog-count">
          {t('design.productCount', { count: visible.length })}
        </p>
      ) : null}
      {visible.length === 0 ? (
        <p className="text-sm text-fg-muted">{t('design.noMatch')}</p>
      ) : (
        <ProductGrid
          products={page}
          testId="product-grid"
          columns={s.columns}
          variant={s.variant}
          look={s}
          mobileColumns={s.mobileColumns}
          quickAdd={s.showAdd}
        />
      )}
      {page.length < visible.length ? (
        <button
          type="button"
          onClick={() => setPages((n) => n + 1)}
          className="mx-auto rounded-full border border-border bg-surface px-6 py-2.5 text-sm font-semibold hover:border-primary"
        >
          {t('design.showMore', { count: visible.length - page.length })}
        </button>
      ) : null}
    </div>
  );
}
